import { EventName } from '@/constants';
import { WebSocketUserItem } from '@/models/webSocketUser';
import { createLoggedAgentsAdminsPageHeaders } from '@/pages/system/loggedAgentsAdmins/LoggedAgentsAdminsPageHeaders';
import eventBus from '@/services/eventBus';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useWebSocketStore } from '@/stores/webSocket';
import { IPageOptions } from '@/types';
import type { DataTableHeader } from '@/types/vuetify';
import type { ILoggedAgentsAdminsFilters, ILoggedAgentsAdminsStats, IWebSocketUserRow } from '@/types/webSocketUser';

const defaultSortBy = [{ key: 'connectionId', order: 'desc' as const }];

function createLoggedAgentsAdminsStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<WebSocketUserItem[]>([]);
    const total = ref<number>(0);
    const stats = ref<ILoggedAgentsAdminsStats | undefined>(undefined);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();
    const isSubscribed = ref(false);

    // --- filters ---
    const filters = reactive<ILoggedAgentsAdminsFilters>({
        loggedAgentsAdminsSearch: {
            value: null,
            title: 'loggedAgentsAdmins.search',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100000,
        page: 1,
        rowsPerPageItems: [100000],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.loggedAgentsAdminsSearch.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createLoggedAgentsAdminsPageHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    const filteredItems = computed(() => {
        const query = filters.loggedAgentsAdminsSearch.value;
        if (!query || !String(query).trim()) {
            return items.value;
        }
        return items.value.filter((item) => item.matchesSearch(String(query)));
    });

    // --- persistence ---
    const reloadFiltersFromStorage = () => {
        resetFilters();
    };

    // --- actions ---
    function setItemsFromRows(rows: IWebSocketUserRow[]) {
        items.value = rows.map((row) => new WebSocketUserItem(row));
        total.value = rows.length;
        stats.value = { total_users: rows.length };
        loading.value = false;
    }

    function onWebSocketUsers(rows: IWebSocketUserRow[]) {
        setItemsFromRows(rows);
    }

    function subscribe() {
        if (isSubscribed.value) return;
        eventBus.on(EventName.WebSocketUsers, onWebSocketUsers);
        isSubscribed.value = true;
    }

    function unsubscribe() {
        if (!isSubscribed.value) return;
        eventBus.off(EventName.WebSocketUsers, onWebSocketUsers);
        isSubscribed.value = false;
    }

    async function loadData() {
        const loadId = loadGeneration.next();
        subscribe();
        loading.value = true;

        const webSocketStore = useWebSocketStore();
        const sent = webSocketStore.sendObj({ event: 'getWebSocketUsers' });

        if (!sent && loadGeneration.isLatest(loadId)) {
            loading.value = false;
        }
    }

    function reloadTool(connectionId: number) {
        const webSocketStore = useWebSocketStore();
        return webSocketStore.sendObj({
            event: 'reloadTool',
            connectionIds: String(connectionId),
        });
    }

    function logoutUser(connectionId: number) {
        const webSocketStore = useWebSocketStore();
        return webSocketStore.sendObj({
            event: 'logoutUsers',
            connectionIds: String(connectionId),
        });
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
        }
        if (value.sortBy !== undefined) {
            options.value.sortBy = value.sortBy;
        }
    }

    function clearData() {
        unsubscribe();
        items.value = [];
        total.value = 0;
        stats.value = undefined;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        filteredItems,
        total,
        stats,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        reloadTool,
        logoutUser,
        updateOptions,
        clearData,
        clientSideFilters: true,
        hasRefresh: true,
    };
}

export function useLoggedAgentsAdminsStore(storeId: string = 'loggedAgentsAdmins') {
    const useStore = defineStore(storeId, () => createLoggedAgentsAdminsStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'loggedAgentsAdmins', defaultSortBy),
    });
    return useStore();
}
